/**
 * PublicConfigContext — React access to platform settings.
 *
 * Wrap the app root with <PublicConfigProvider>. Components call
 * usePublicConfig() to get values + loading/refresh.
 *
 * The module-level cache in src/lib/publicConfig.js is the source of truth;
 * this context just exposes it to React + triggers re-renders on refresh.
 */

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { loadPublicConfig, getConfig, refreshPublicConfig, getAllConfig } from '@/lib/publicConfig';

const Ctx = createContext(null);

export function PublicConfigProvider({ children }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [version, setVersion] = useState(0); // bump to force re-reads from cache

  useEffect(() => {
    let cancelled = false;
    loadPublicConfig()
      .then(() => { if (!cancelled) { setLoading(false); setVersion(v => v + 1); } })
      .catch((err) => { if (!cancelled) { setError(err); setLoading(false); } });
    return () => { cancelled = true; };
  }, []);

  const refresh = useCallback(async () => {
    await refreshPublicConfig();
    setVersion(v => v + 1);
  }, []);

  const value = useMemo(() => ({
    loading,
    error,
    version,
    refresh,
    get: (key, defaultValue) => getConfig(key, defaultValue),
    all: () => getAllConfig(),
  }), [loading, error, version, refresh]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function usePublicConfig() {
  const ctx = useContext(Ctx);
  if (!ctx) {
    // Safe fallback: reads still work (via module cache + env) without provider
    return {
      loading: false,
      error: null,
      version: 0,
      refresh: async () => {},
      get: (key, defaultValue) => getConfig(key, defaultValue),
      all: () => getAllConfig(),
    };
  }
  return ctx;
}

/**
 * Convenience hook for a single key with reactive re-renders.
 */
export function usePublicConfigValue(key, defaultValue) {
  const { version, get } = usePublicConfig();
  // version included in deps so updates re-read cache
  return useMemo(() => get(key, defaultValue), [version, key, defaultValue, get]);
}
